import { initOptionSelects } from '@theme/product-option-selects';

/**
 * Product-card inline option dropdowns.
 *
 * Initialises the shared option-select dropdowns inside a product card and
 * keeps the hidden variant-ID input, ATC button, and card image in sync
 * with the currently selected variant.
 *
 * Used by the `option-dropdowns` block type inside `_product-card`.
 */
class ProductCardOptions extends HTMLElement {
  /** @type {{ destroy: () => void } | null} */
  #selectsHandle = null;

  connectedCallback() {
    this.#selectsHandle = initOptionSelects(this, {
      onVariantChange: (variant) => {
        if (!variant) return;
        this.#updateVariantInput(variant);
        this.#updateATCButton(variant);
        this.#updateImage(variant);
      },
    });
  }

  disconnectedCallback() {
    this.#selectsHandle?.destroy();
    this.#selectsHandle = null;
  }

  /**
   * Updates the hidden variant-ID input and dispatches a change event.
   * @param {Record<string, unknown>} variant
   */
  #updateVariantInput(variant) {
    const variantInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[ref="variantId"]')
    );
    if (variantInput) {
      variantInput.value = String(variant.id);
      variantInput.disabled = !variant.available;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Enables/disables the ATC button based on variant availability.
   * @param {Record<string, unknown>} variant
   */
  #updateATCButton(variant) {
    const addBtn = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-testid="product-card-options-add-to-cart"]')
    );
    if (addBtn) {
      addBtn.disabled = !variant.available;
    }
  }

  /**
   * Swaps the product card image to the variant's featured image (or default).
   * Looks for the image inside the parent product-card element's gallery.
   * @param {Record<string, unknown>} variant
   */
  #updateImage(variant) {
    const card = this.closest('product-card');
    if (!card) return;

    const featuredImage = /** @type {Record<string, unknown> | null} */ (
      variant.featured_image ?? null
    );
    const newSrc =
      typeof featuredImage?.src === 'string' && featuredImage.src.length > 0
        ? featuredImage.src
        : null;

    // Find the primary card image in the gallery
    const img = /** @type {HTMLImageElement | null} */ (
      card.querySelector('.card-gallery .product-media__image')
    );

    if (img) {
      const fallbackSrc = this.dataset.defaultImageSrc?.trim() ?? '';
      const src = newSrc ?? fallbackSrc;
      if (src) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = src;
      }
    }
  }
}

if (!customElements.get('product-card-options')) {
  customElements.define('product-card-options', ProductCardOptions);
}
